"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { cn } from "@/lib/utils";
import { SanityImage } from "@/components/atoms/SanityImage";
import { TeamMemberExpanded } from "./TeamGrid.props";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";

/**
 * Properties for the TeamMemberCard component.
 * @interface TeamMemberCardProps
 * @property {TeamMemberExpanded} member - The team member data to display.
 * @property {boolean} isActive - Whether the card is currently selected/active.
 * @property {() => void} onClick - Callback function when the card is clicked.
 */
interface TeamMemberCardProps {
  member: TeamMemberExpanded;
  isActive: boolean;
  onClick: () => void;
}

/**
 * TeamMemberCard component displays an individual team member's headshot, name, and title.
 * Features premium hover and active state styling.
 *
 * @param {TeamMemberCardProps} props - The properties for the TeamMemberCard component.
 * @returns {JSX.Element} The rendered TeamMemberCard component.
 */
export const TeamMemberCard = t0 => {
  const $ = _c(25);
  const {
    member,
    isActive,
    onClick
  } = t0;
  const t1 = isActive ? "bg-accent shadow-[var(--aq-shadow-card-lift)] ring-1 ring-white/20 scale-102" : "hover:bg-white/5 hover:shadow-xl";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("group cursor-pointer rounded-2xl p-4 transition-all duration-500", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = member.image;
  const t4 = isActive ? "scale-105" : "group-hover:scale-110";
  let t5;
  if ($[2] !== t4) {
    t5 = cn("h-full w-full object-cover transition-transform duration-500", t4);
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== member.image || $[5] !== t5) {
    t6 = <Box className="relative aspect-portrait overflow-hidden rounded-xl bg-white/5 mb-6"><SanityImage image={t3} fill={true} aspectRatio="4/5" sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw" className={t5} /></Box>;
    $[4] = member.image;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = isActive ? "text-white" : "text-foreground group-hover:text-primary";
  let t8;
  if ($[7] !== t7) {
    t8 = cn("font-bold text-lg leading-tight transition-colors", t7);
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== member.name || $[10] !== t8) {
    t9 = <Text as="p" className={t8}>{member.name}</Text>;
    $[9] = member.name;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = isActive ? "text-white/70" : "text-muted-foreground";
  let t11;
  if ($[12] !== t10) {
    t11 = cn("text-sm font-medium transition-colors", t10);
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== member.title || $[15] !== t11) {
    t12 = <Text as="p" className={t11}>{member.title}</Text>;
    $[14] = member.title;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12 || $[18] !== t9) {
    t13 = <Box className="space-y-1">{t9}{t12}</Box>;
    $[17] = t12;
    $[18] = t9;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== onClick || $[21] !== t13 || $[22] !== t2 || $[23] !== t6) {
    t14 = <Box onClick={onClick} className={t2}>{t6}{t13}</Box>;
    $[20] = onClick;
    $[21] = t13;
    $[22] = t2;
    $[23] = t6;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  return t14;
};